import React, { useState, useEffect } from 'react'
import * as Sentry from '@sentry/browser'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { ShippingAddressInput, MagentoAvailableShippingMethod as AvailableShippingMethod } from 'lib/types'
import { addressValidation } from 'lib/core/data/schema'
import { DEFAULT_COUNTRY_CODE } from 'lib/core/resources/constants'
import { localStorage } from 'lib/core/services/localStorage'
import { addressMatch } from 'lib/core/resources/helpers'
import { useCustomer, useDirectoryOptions } from 'lib/core/hooks'
import { Alert, Form, Grid, Toast } from 'lib/ui/components'
import {
  useCheckout,
  useCheckoutContext,
  AddressPicker,
  ShippingMethodItem
} from 'lib/modules/checkout'
import { schema, validation, Values } from './schema'

import { Actions } from './components/Actions'
import { Container, ShippingMethodsContainer } from './styled'
import { useShippingRegionOptions } from '../../../../../../../../hooks/useShippingRegionOptions'

const FREE_SHIPPING = 'freeshipping'
export const ShippingForm = () => {
  const intl = useIntl()
  const { addresses, defaultShippingAddress } = useCustomer()
  const { setState, state: checkoutState } = useCheckoutContext()
  const {
    setShippingAddress,
    setShippingMethod,
    validateShippingAddress,
    shippingMethod,
    shippingAddress,
    hasShippingMethods,
    checkoutSchema
  } = useCheckout()

  const hasValidShippingAddress = validateShippingAddress()
  const [isFormValid, setIsFormValid] = useState(hasValidShippingAddress)

  const address = checkoutSchema?.shippingAddress
  const availableShippingMethods = address?.availableShippingMethods

  const [addressId, setAddressId] = useState<number | undefined>()
  const [currentAddressId] = useState<number | undefined>(
    !addressId
      ? addresses?.find(element => addressMatch(element, address))?.id
      : undefined
  )

  const [defaultAddressId] = useState<number | undefined>(
    defaultShippingAddress && !addressId && !currentAddressId && !addressValidation().isValidSync(address)
      ? defaultShippingAddress.id
      : undefined
  )
  const [countryCode, setCountryCode] = useState<string>(address?.countryCode ? address.countryCode : DEFAULT_COUNTRY_CODE)

  const {
    countryInfoOptions,
    countryInfoIsFetching
  } = useDirectoryOptions({ countryCode })
  const { shippingRegionOptions: regionOptions } = useShippingRegionOptions({ countryCode })

  const shippingMethodCode = address?.selectedShippingMethod?.method_code ?? FREE_SHIPPING ?? undefined
  const [selectedShippingMethodCode, setSelectedShippingMethodCode] = useState<string | undefined>(shippingMethodCode)
  const freeShippingMethod = availableShippingMethods?.find(method => method.method_code === FREE_SHIPPING)
  const isLoading = shippingMethod.isFetching || shippingAddress.isFetching

  const handleCountryChange = ({ value, name, form }: any) => { // TODO: Type
    const { setFieldValue, setFieldTouched } = form
    setCountryCode(value)
    setFieldValue('regionCode', '', false)
    setFieldTouched('regionCode', false, false)
  }

  const handleShippingMethodChange = (method: AvailableShippingMethod) => {
    const { method_code: methodCode, carrier_code: carrierCode } = method
    setSelectedShippingMethodCode(methodCode)
    setShippingMethod({ carrierCode, methodCode }, {
      debounce: true,
      callback: {
        onFailure: (params) => {
          setSelectedShippingMethodCode(undefined)
          Toast.toastFailure({ messageId: 'checkout.shippingMethod.failure' })
          Sentry.withScope((scope) => {
            const user = localStorage.get('user')
            scope.setUser(user)
            scope.setExtras({
              ...params?.extraData,
              user
            })
            Sentry.captureException(params?.error)
          })
        }
      }
    })
  }

  const handleShippingAddress = (values: Values) => {
    const {
      firstName,
      lastName,
      company,
      street,
      street2,
      city,
      regionCode,
      postalCode,
      countryCode,
      phoneNumber
    } = values

    const shippingAddress: ShippingAddressInput = {
      address: {
        city,
        countryCode,
        company,
        firstName,
        lastName,
        postalCode,
        regionCode,
        street,
        street2,
        phoneNumber
      }
    }

    setShippingAddress({ shippingAddress }, {
      debounce: true,
      callback: {
        onSuccess: () => {
          setState((state) => ({
            ...state,
            shippingErrors: {}
          }))
        },
        onFailure: (params) => {
          Toast.toastFailure({ messageId: 'checkout.shippingAddress.failure' })
          Sentry.withScope((scope) => {
            const user = localStorage.get('user')
            scope.setUser(user)
            scope.setExtras({
              ...params?.extraData,
              user
            })
            Sentry.captureException(params?.error)
          })
        }
      }
    })
  }

  useEffect(() => {
    setSelectedShippingMethodCode(shippingMethodCode)
  }, [shippingMethodCode, setSelectedShippingMethodCode])

  useEffect(() => {
    if (freeShippingMethod) {
      handleShippingMethodChange(freeShippingMethod)
    }
    // eslint-disable-next-line
  }, [hasShippingMethods])

  useEffect(() => {
    if (currentAddressId) {
      setAddressId(currentAddressId)
    }
  }, [
    setAddressId,
    currentAddressId
  ])

  useEffect(() => {
    if (defaultAddressId) {
      setAddressId(defaultAddressId)
    }
  }, [
    setAddressId,
    defaultAddressId
  ])

  return (
    <Container>
      <Formik
        onSubmit={handleShippingAddress}
        validationSchema={validation({ intl })}
        initialValues={{
          ...schema,
          ...address,
          countryCode
        }}
      >
        <Form.Wrapper>
          <Form.FormikObserver
            autoSubmit
            onError={(errors) => {
              setState((state) => ({
                ...state,
                shippingErrors: errors
              }))
              setIsFormValid(false)
            }}
            onValid={() => {
              setIsFormValid(true)
            }}
          />

          <h2>
            Shipping Information
          </h2>

          {!isFormValid && checkoutState.shippingErrors?.street && (
            <Alert type='danger'>
              {checkoutState.shippingErrors?.street}
            </Alert>
          )}

          <AddressPicker
            value={addressId}
            addresses={addresses}
            onChange={(values) => setAddressId(values?.id)}
            useAnotherLabel='Use another shipping address'
          />

          {!addressId && (
            <>
              <Grid>
                <div>
                  <Field
                    name='firstName'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.firstName'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                    maxLength='20'
                  />
                </div>
                <div>
                  <Field
                    name='lastName'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.lastName'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                    maxLength='20'
                  />
                </div>
              </Grid>
              <Grid>
                <Field
                  name='company'
                  label={intl.formatMessage({
                    id: 'checkout.shipping.label.company'
                  })}
                  component={Form.Input}
                  hasAutoPlaceholder
                />

                <Field
                  name='phoneNumber'
                  label={intl.formatMessage({
                    id: 'checkout.shipping.label.phoneNumber'
                  })}
                  component={Form.Input}
                  hasAutoPlaceholder
                  isRequired
                />
              </Grid>

              <Field
                name='street'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.street'
                })}
                component={Form.Input}
                hasAutoPlaceholder
                isRequired
                maxLength='30'
              />

              <Field
                name='street2'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.street2'
                })}
                component={Form.Input}
                hasAutoPlaceholder
                maxLength='30'
              />

              <Grid>
                <div>
                  <Field
                    name='city'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.city'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                    maxLength='20'
                  />
                </div>
                <div>
                  <Field
                    name='regionCode'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.region'
                    })}
                    component={Form.Select}
                    options={regionOptions}
                    isLoading={countryInfoIsFetching}
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='postalCode'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.postalCode'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                    maxLength='5'
                  />
                </div>
              </Grid>

              <Field
                name='countryCode'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.country'
                })}
                component={Form.Select}
                options={countryInfoOptions}
                interceptChange={handleCountryChange}
                isLoading={countryInfoIsFetching}
                isRequired
              />
            </>
          )}

          <h2>
            Shipping Method
          </h2>

          <ShippingMethodsContainer
            isLoading={isLoading}
          >
            {isLoading && !hasShippingMethods && (
              <Alert>
                <FormattedMessage id='app.loading' />
              </Alert>
            )}

            {!isLoading && !hasValidShippingAddress && !hasShippingMethods && (
              <Alert>
                Set your shipping information to see the available shipping methods
              </Alert>
            )}

            {!isLoading && hasValidShippingAddress && !hasShippingMethods && (
              <Alert>
                No shipping methods available for the current address
              </Alert>
            )}

            {availableShippingMethods?.map(method => {
              const { method_code: methodCode } = method
              const isSelected = methodCode === selectedShippingMethodCode || methodCode === FREE_SHIPPING

              return (
                <ShippingMethodItem
                  key={`${methodCode}-${method.price_incl_tax.value}`}
                  method={method}
                  isSelected={isSelected}
                  onChange={handleShippingMethodChange}
                />
              )
            })}
          </ShippingMethodsContainer>

          <Actions isFormValid={isFormValid} />
        </Form.Wrapper>
      </Formik>
    </Container>
  )
}
